// ============================================================
// fondos/efectos/dot-grid.js — «Dot Grid»: rejilla de puntos que respira y reacciona al puntero
// (efecto de LIENZO)
//
// Portado de `vendor/bg/dot-grid-bg.js` en la F2b del P3 con el MISMO dibujo (el test de espejo lo
// compara llamada a llamada con la clase de siempre): sin puntero, una onda de brillo recorre la
// rejilla; con él, los puntos cercanos crecen, se iluminan y se apartan. El puntero llega ahora desde
// la SECCIÓN (`fondos/lienzo.js`), así que esa reacción por fin se ve (E12). Avanza por RELOJ (la onda
// es función de `t`), no por pasos: su velocidad nunca dependió de los fps, solo su suavidad.
// Ningún punto pasa del tope del catálogo (`alfaMax`, F3): el que ilumina el puntero llega a él.
// ============================================================

import { rgbDeBase } from './comun.js';

/** Radio (px CSS) de influencia del puntero. */
const INFLUENCIA = 130;

/** Tope de puntos de la rejilla completa: por encima, se abre el espaciado. */
const TOPE_PUNTOS = 800;

/** La rejilla centrada en la caja, con el espaciado abierto hasta no pasar del tope. */
function rejilla(s) {
    let sp = s.spacing || 40;
    let cols = Math.floor(s.w / sp), rows = Math.floor(s.h / sp);
    while ((cols + 1) * (rows + 1) > TOPE_PUNTOS && sp < 220) {
        sp += 6;
        cols = Math.floor(s.w / sp);
        rows = Math.floor(s.h / sp);
    }
    const offX = (s.w - cols * sp) / 2 + sp / 2;
    const offY = (s.h - rows * sp) / 2 + sp / 2;
    s.points = [];
    for (let r = 0; r <= rows; r++) {
        for (let c = 0; c <= cols; c++) {
            s.points.push({ x: offX + c * sp, y: offY + r * sp });
        }
    }
}

export default {
    crear(ctx, e) {
        const p = e.params;
        const dn = p.density != null ? p.density : 5;
        const s = {
            w: e.w, h: e.h, rgb: rgbDeBase(e.c1),
            baseAlpha: e.oscuro ? 0.5 : 0.32,
            tope: e.alfa,
            // Densidad 1-10 → espaciado 64-30 px (+16 en táctil: menos puntos).
            spacing: Math.round(64 - (dn - 1) * (34 / 9)) + (e.tactil ? 16 : 0),
            // OJO: su velocidad por defecto es 4, no 5.
            waveSpeed: (p.speed != null ? p.speed : 4) * 0.18,
            points: null,
        };
        rejilla(s);
        return s;
    },

    redimensionar(s, w, h) {
        s.w = w;
        s.h = h;
        rejilla(s);
    },

    dibujar(s, ctx, t, dt, puntero) {
        const rgb = s.rgb;
        if (!s.points) return;
        const time = t * 0.001;
        const influSq = INFLUENCIA * INFLUENCIA;

        for (let i = 0; i < s.points.length; i++) {
            const p = s.points[i];
            let size = 1.4;
            let alpha = s.baseAlpha;
            let dx = 0, dy = 0;

            if (puntero) {
                const ddx = p.x - puntero.x, ddy = p.y - puntero.y;
                const dSq = ddx * ddx + ddy * ddy;
                if (dSq < influSq) {
                    const d = Math.sqrt(dSq) || 0.001;
                    const f = 1 - d / INFLUENCIA;              // 0..1
                    size += f * 3.2;                            // crece
                    alpha = Math.min(s.tope, alpha + f * 0.55); // se ilumina, hasta el tope
                    const push = f * 10;                        // se aparta
                    dx = (ddx / d) * push;
                    dy = (ddy / d) * push;
                }
            } else {
                // Onda en reposo (también en táctil).
                const onda = Math.sin((p.x + p.y) * 0.01 + time * s.waveSpeed);
                alpha = Math.min(s.tope, alpha + onda * 0.16);
                size += (onda + 1) * 0.5;
            }

            ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
            ctx.beginPath();
            ctx.arc(p.x + dx, p.y + dy, size, 0, Math.PI * 2);
            ctx.fill();
        }
    },
};
